import { useParams } from 'react-router-dom'
import {
  api,
  type CourseHole,
  type CourseValues,
  type EventSummary,
  type GoverningValues,
} from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { YardageBook } from '../../components/YardageBook'

/**
 * The pre-round stroke sheet (FR-024e to FR-024j).
 *
 * # Where the shots fall, not merely how many
 *
 * FR-024e, and it is the whole point: knowing you get three is useless at the
 * tee of the fourth. The two players of each match appear SIDE BY SIDE, hole by
 * hole, so where one receives and the other does not is something you look at
 * rather than work out (FR-024g). The league relies on this today.
 *
 * # Provisional means the index, and nothing else (FR-024i)
 *
 * The course data behind the sheet is already final — the season's values
 * govern the played round (FR-007b), so nothing about the course can change
 * between this projection and the result (SC-013g). The only thing that can
 * still move is a player's Handicap Index, if they record another round before
 * Start Round. The banner says exactly that, because "provisional" on its own
 * invites the reader to distrust the whole page.
 */
export function StrokeSheet() {
  const { eventId } = useParams()
  const id = Number(eventId)
  const sheet = useApi((s) => api.strokeSheet(id, s), [id])

  if (sheet.state === 'loading') return <Loading what="the stroke sheet" />
  if (sheet.state === 'error') return <Failed what="The stroke sheet" error={sheet.error} />

  const { event, course, provisional, provisional_because, pairings, unpaired, differences } =
    sheet.data

  return (
    <section>
      <h2>
        Stroke sheet — week {event.week_number}, {course.course_key}
      </h2>

      <CourseHeader event={event} course={course} />

      {provisional ? (
        <p className="note note-provisional" data-testid="provisional">
          <strong>Provisional.</strong>{' '}
          {provisional_because ??
            'Only a player’s Handicap Index can still move; the course data is already final.'}
        </p>
      ) : (
        <p className="note" data-testid="recorded">
          <strong>Recorded.</strong> These are the values that governed the round, frozen at Start
          Round — not a recomputation.
        </p>
      )}

      {differences.length > 0 && (
        <div className="note note-warn" data-testid="differences">
          <p>These differ from what the sheet last showed:</p>
          <ul>
            {differences.map((d) => (
              <li key={d.player_id}>
                <strong>{d.display_name}</strong> — {d.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pairings.length === 0 && unpaired.length === 0 && (
        <Empty>No handicaps have been worked out for this event yet.</Empty>
      )}

      <div className="stroke-pairs">
        {pairings.map((p) => (
          <Pairing key={p.match_id} a={p.player_a} b={p.player_b} card={course.card} />
        ))}
      </div>

      {unpaired.map((p) => (
        <article key={p.player_id} className="stroke-pair">
          <h3>{p.display_name} — bye</h3>
          <p className="note">The bye player still plays, and is still eligible for the low net.</p>
          <Derivation g={p} />
          <SingleAllocation g={p} card={course.card} />
        </article>
      ))}

      <YardageBook
        event={event}
        course={course}
        players={[...pairings.flatMap((p) => [p.player_a, p.player_b]), ...unpaired]}
      />
    </section>
  )
}

/**
 * What the sheet is derived from, stated once.
 *
 * Course Handicap is `Index × Slope/113 + (Rating − Par)`. Every figure below
 * comes out of those four numbers, so FR-024f's "show the full derivation" is
 * not satisfied by the per-player rows alone — the inputs common to everyone
 * have to be on the page too, or the arithmetic cannot be repeated by hand.
 *
 * # It also makes a basis mismatch visible
 *
 * A course rating is an EIGHTEEN-hole figure; a par may be stated over nine or
 * eighteen. When they disagree, `Rating − Par` is wrong by the difference and
 * every player's handicap moves by the same amount — invisible in a match,
 * because both sides move together, and plain here once the two numbers sit
 * next to each other.
 */
function CourseHeader({ event, course }: { event: EventSummary; course: CourseValues }) {
  return (
    <dl className="course-header" data-testid="course-header">
      <div>
        <dt>Course</dt>
        <dd>{course.course_key}</dd>
      </div>
      <div>
        <dt>Tee</dt>
        <dd>{course.tee_name}</dd>
      </div>
      <div>
        <dt>Rating</dt>
        <dd>{course.course_rating}</dd>
      </div>
      <div>
        <dt>Slope</dt>
        <dd>{course.slope_rating}</dd>
      </div>
      <div>
        {/* The par of the holes DESCRIBED. The formula converts it to the
            rating's eighteen-hole basis first: on a nine they are different
            numbers, and subtracting one from the other unconverted is what
            once put a 2.5 index on 39 strokes. */}
        <dt>Par</dt>
        <dd>
          {course.par} / {event.holes_expected}
        </dd>
      </div>
      <div>
        <dt>Date</dt>
        <dd>
          <time dateTime={event.scheduled_date}>{event.scheduled_date}</time>
        </dd>
      </div>
    </dl>
  )
}

/**
 * Two players of one match, hole by hole.
 *
 * # The stroke index is the reason, and it is on the page
 *
 * Allocation is by RANK over the holes actually played (FR-008-F): the first
 * stroke goes to the hardest hole, the second to the next, and a handicap
 * larger than the hole count wraps round and starts again. Showing the strokes
 * without the index shows the result and hides the rule, which is the half a
 * player checking the sheet actually needs.
 *
 * # A hole with no stroke is blank, not zero
 *
 * "0" reads as a value that was computed; blank reads as nothing to receive,
 * which is what it is. The holes a player DOES receive on are the marked ones,
 * and that contrast is the whole purpose of the table (FR-024g).
 */
function Pairing({ a, b, card }: { a: GoverningValues; b: GoverningValues; card: CourseHole[] }) {
  const holes =
    card.length > 0
      ? card.map((h) => h.hole_number)
      : [...new Set([...a.stroke_allocation, ...b.stroke_allocation].map((s) => s.hole))].sort(
          (x, y) => x - y,
        )
  const indexOf = new Map(card.map((h) => [h.hole_number, h.stroke_index]))
  const strokesOf = (g: GoverningValues, hole: number) =>
    g.stroke_allocation.find((s) => s.hole === hole)?.strokes ?? '0'

  return (
    <article className="stroke-pair" data-testid="pairing">
      <h3>
        {a.display_name} v {b.display_name}
      </h3>
      <div className="stroke-pair-derivations">
        <Derivation g={a} />
        <Derivation g={b} />
      </div>
      <div className="scroll-x">
        <table className="table stroke-table">
          <thead>
            <tr>
              <th scope="col">Hole</th>
              {holes.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="stroke-index-row">
              <th scope="row" title="Stroke index — the hole's difficulty rank">
                SI
              </th>
              {holes.map((h) => (
                <td key={h}>{indexOf.get(h) ?? '–'}</td>
              ))}
            </tr>
            {[a, b].map((g) => (
              <tr key={g.player_id}>
                <th scope="row">{g.display_name}</th>
                {holes.map((h) => {
                  const strokes = strokesOf(g, h)
                  const receives = strokes !== '0'
                  return (
                    <td
                      key={h}
                      className={receives ? 'receives' : 'no-stroke'}
                      title={
                        receives
                          ? `${g.display_name} receives ${strokes} on hole ${h} (stroke index ${indexOf.get(h) ?? '?'})`
                          : `${g.display_name} receives nothing on hole ${h}`
                      }
                    >
                      {receives ? strokes : ''}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="stroke-legend">
        <span className="swatch" /> a stroke received · blank · none · SI is the hole&apos;s
        difficulty rank, and strokes go to the lowest first
      </p>
    </article>
  )
}

function SingleAllocation({ g, card }: { g: GoverningValues; card: CourseHole[] }) {
  if (g.stroke_allocation.length === 0) return null
  const holes =
    card.length > 0 ? card.map((h) => h.hole_number) : g.stroke_allocation.map((s) => s.hole)
  const indexOf = new Map(card.map((h) => [h.hole_number, h.stroke_index]))
  const strokesOf = (hole: number) =>
    g.stroke_allocation.find((s) => s.hole === hole)?.strokes ?? '0'

  return (
    <div className="scroll-x">
      <table className="table stroke-table">
        <thead>
          <tr>
            <th scope="col">Hole</th>
            {holes.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="stroke-index-row">
            <th scope="row">SI</th>
            {holes.map((h) => (
              <td key={h}>{indexOf.get(h) ?? '–'}</td>
            ))}
          </tr>
          <tr>
            <th scope="row">{g.display_name}</th>
            {holes.map((h) => {
              const strokes = strokesOf(h)
              const receives = strokes !== '0'
              return (
                <td key={h} className={receives ? 'receives' : 'no-stroke'}>
                  {receives ? strokes : ''}
                </td>
              )
            })}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/**
 * The full derivation (FR-024f, FR-023d).
 *
 * Index, course handicap, allowance, playing handicap — every step, so the
 * figure can be checked by hand. FR-023d asks for exactly that, and a total
 * with no working is a number you can only take on trust.
 */
function Derivation({ g }: { g: GoverningValues }) {
  return (
    <dl className="derivation" data-testid="derivation">
      <div>
        <dt>{g.display_name}</dt>
        <dd />
      </div>
      <div>
        <dt>Index</dt>
        <dd>{g.handicap_index}</dd>
      </div>
      <div>
        <dt>Course handicap</dt>
        <dd>{g.course_handicap}</dd>
      </div>
      <div>
        <dt>Allowance</dt>
        <dd>{g.allowance_pct}%</dd>
      </div>
      <div>
        <dt>Playing handicap</dt>
        <dd>{g.playing_handicap}</dd>
      </div>
      {g.handicap_adjustment !== '0' && (
        <div>
          <dt>Season-points bonus</dt>
          <dd>{g.handicap_adjustment}</dd>
        </div>
      )}
      <div>
        <dt>{g.playing_handicap_overridden ? 'Overridden to (18)' : 'Course handicap (18)'}</dt>
        <dd data-testid="derivation-allocated-from">
          {g.adjusted_playing_handicap}
          {/* An override replaces the figure the strokes come from, so the
              working above it no longer leads here; saying so is what keeps
              the arithmetic readable (FR-008-L). */}
          {g.playing_handicap_overridden && (
            <span className="note"> — set by the commissioner, not from the index</span>
          )}
        </dd>
      </div>
      {/* The last step, and the one a nine-hole sheet is unreadable without:
          an eighteen-hole handicap allocated across eighteen holes leaves a
          different number on the nine actually played (FR-008-H). */}
      <div>
        <dt>Receives</dt>
        <dd className="strong">
          {g.strokes_received} on these {g.holes_played} holes
        </dd>
      </div>
    </dl>
  )
}
